import { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes as ReactRoutes,
  useLocation,
  useNavigate,
  matchPath,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loading from "../components/shared/Loading/Loading";

const EmptyLayout = ({ children }) => <>{children}</>;

const ProtectedContent = ({ auth, checkDone, session, retry, redirecting, children }) => {
  const { t } = useTranslation();
  if (!checkDone) return <Loading />;
  if (!auth) return <Navigate to="/login" replace />;
  if (session.status === "error") {
    return (
      <div role="alert" className="p-4 text-center">
        <p>{t("authSession.loadFailed")}</p>
        <button type="button" className="btn btn-primary" onClick={retry}>
          {t("authSession.retry")}
        </button>
      </div>
    );
  }
  if (session.status !== "ready" || redirecting) return <Loading />;
  return children;
};

const Routes = ({ routes, auth, checkDone, session, retry, userType }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [redirectedSessionId, setRedirectedSessionId] = useState(null);
  const privateLocation = routes.some(
    (route) =>
      route.Private &&
      (matchPath(route.path, location.pathname) ||
        route.modules?.some((child) => matchPath(route.path + child.path, location.pathname))),
  );
  const redirecting = !!session.external && redirectedSessionId !== session.sessionId && privateLocation;
  useEffect(() => {
    if (session.status !== "ready" || !session.external || redirectedSessionId === session.sessionId) return;
    if (privateLocation) {
      navigate(String(userType).toLowerCase() === "expeditor" ? "/orders/available" : "/main", {
        replace: true,
      });
    }
    setRedirectedSessionId(session.sessionId);
  }, [
    session.status,
    session.external,
    session.sessionId,
    redirectedSessionId,
    privateLocation,
    userType,
    navigate,
  ]);

  const renderPage = (Component, Layout, isPrivate) => {
    const page = (
      <Layout>
        <Component />
      </Layout>
    );
    return isPrivate ? (
      <ProtectedContent
        key={session.sessionId || "anonymous"}
        auth={auth}
        checkDone={checkDone}
        session={session}
        retry={retry}
        redirecting={redirecting}
      >
        {page}
      </ProtectedContent>
    ) : (
      page
    );
  };
  return (
    <ReactRoutes location={location}>
      {routes.map(({ Private, Layout = EmptyLayout, modules, Component, path }) => [
        <Route key={path} path={path} element={renderPage(Component, Layout, Private)} />,
        ...(modules || []).map((child) => (
          <Route
            key={path + child.path}
            path={path + child.path}
            element={renderPage(child.Component, Layout, Private)}
          />
        )),
      ])}
      <Route path="*" element={<Navigate to="/main" replace />} />
    </ReactRoutes>
  );
};
export default Routes;
